import * as React from 'react';
import "./assets/DualValueFormElement.css";
import { IFormElementConfig } from "./assets/BusinessObjects";
import { eFormElementType } from "../../services/Enums/shared";
import { getTableValuesByGroupName } from "../../services/Utils/IndexedDBUtils";
import { IFormioValidationError } from '../../common/sharedTypes';
import TooltipToggle from '../shared/TooltipToggle/TooltipToggle';

interface IDynamicFormElementProps {
  config: IFormElementConfig;
  readOnly: boolean;
  onChange: (event: React.ChangeEvent) => void;
  validationErrors?: IFormioValidationError[];
  hasAnyFormErrors?: boolean;
}

interface IDynamicFormElementState {
  showDataCorrectionElement: boolean;
  dropdownValues: { name: string; description: string }[];
  isLoading: boolean;

  // New: lock the ORIGINAL (read-only) dropdown value/label once
  originalLockedOption: { name: string; description: string } | null;
  originalLocked: boolean;

  // Map-it style edit toggle for editable text areas (e.g. Location Description)
  isEditingTextArea: boolean;
  showLengthError: boolean;
}

export default class DynamicFormElement extends React.Component<IDynamicFormElementProps, IDynamicFormElementState> {
  private static readonly maxEditableTextAreaLength = 100;

  constructor(props: any) {
    super(props);
    const hasCorrected =
      typeof props.config.correctedValue === 'string' &&
      props.config.correctedValue.trim() !== '';

    this.state = {
      showDataCorrectionElement: hasCorrected,
      dropdownValues: [],
      isLoading: false,
      originalLockedOption: null,
      originalLocked: false,
      isEditingTextArea: false,
      showLengthError: false
    };
  }

  // Mirrors the map-it Location Description behavior: cap the value at 100
  // characters and briefly surface an error when the user exceeds it.
  handleEditableTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (event.currentTarget.value.length <= DynamicFormElement.maxEditableTextAreaLength) {
      this.props.onChange(event);
    } else {
      this.setState({ showLengthError: true }, () => {
        setTimeout(() => this.setState({ showLengthError: false }), 5000);
      });
    }
  };

  async componentDidMount() {
    await this.loadInitialDropdownValues();
  }

  async componentDidUpdate(prevProps: IDynamicFormElementProps) {
    // Reload if the group changed
    if (
      this.props.config.inputType === eFormElementType.Dropdown &&
      prevProps.config.dropdownGroupName !== this.props.config.dropdownGroupName
    ) {
      await this.loadInitialDropdownValues();
    }
  }

  private async loadInitialDropdownValues() {
    const { config } = this.props;

    if (config.inputType === eFormElementType.Dropdown && config.dropdownGroupName) {
      this.setState({ isLoading: true });
      try {
        const results = await getTableValuesByGroupName(config.dropdownGroupName, false);
        this.rebindDropdownValues(results); // programmatic load — not from user
      } catch (e) {
        console.error(`[DVE] Failed loading options for ${config.dropdownGroupName}`, e);
        this.setState({ dropdownValues: [], isLoading: false });
      }
    } else {
      this.setState({ isLoading: false });
    }
  }

  showCorrectedElement = () => {
    this.setState({ showDataCorrectionElement: true });
  };

  /**
   * Rebind dropdown options.
   * When opts.fromUser=true (e.g., County changed by user), optionally notify parent to clear
   * corrected value if it no longer exists in the new list.
   */
  rebindDropdownValues = (
    values: any[],
    opts: { fromUser?: boolean } = {}
  ) => {
    const { fromUser = false } = opts;

    // Normalize incoming items to {name, description}
    const normalized: { name: string; description: string }[] = Array.isArray(values)
      ? values.map(v => ({
          name: v?.name ?? v?.value ?? '',
          description: v?.description ?? v?.label ?? String(v?.name ?? v?.value ?? '')
        }))
      : [];

    const currentCorrected = this.props.config.correctedValue ?? '';
    const stillValid = normalized.some(v => String(v.name) === String(currentCorrected));

    this.setState(prev => {
      const next: Partial<IDynamicFormElementState> = {
        dropdownValues: normalized,
        isLoading: false
      };

      // Tell the parent to clear corrected value ONLY for user-initiated reloads
      if (fromUser && currentCorrected !== '' && !stillValid && typeof this.props.onChange === 'function') {
        const evt = { target: { value: "" } } as React.ChangeEvent<any>;
        this.props.onChange(evt);
      }

      // Lock ORIGINAL value/label once (so the display never changes afterward)
      if (!prev.originalLocked) {
        const code = String(this.props.config.originalValue ?? '');
        const match = normalized.find(v => String(v.name) === code);
        const label = match?.description ?? code;

        next.originalLockedOption = code ? { name: code, description: label } : null;
        next.originalLocked = true;
      }

      return next as IDynamicFormElementState;
    });
  };

  public render() {
    const { config } = this.props;
    const validationErrors = this.props.validationErrors ?? [];

    // ---- validations / styles
    let hasError = false;
    let hasWarning = false;
    let errorStyle = "";
    let pertinentValidations: IFormioValidationError[] = [];
    const warningIconClass = 'fas fa-exclamation-triangle ttwarning';
    const errorIconClass = 'fa-solid fa-circle-exclamation tterror';
    let iconClass = '';

    if (
      validationErrors.length > 0 &&
      Array.isArray(config.validationFormIoComponents) &&
      config.validationFormIoComponents.length > 0
    ) {
      pertinentValidations = validationErrors.filter(ve =>
        ve.components?.some(c => config.validationFormIoComponents?.some(vc => vc === c))
      );

      const pertinentErrors = validationErrors.filter(ve =>
        ve.type === 'error' &&
        ve.components?.some(c => config.validationFormIoComponents?.some(vc => vc === c))
      );

      if (pertinentErrors.length > 0) {
        errorStyle = "dveValidationError";
        hasError = true;
        iconClass = errorIconClass;
      }
    }

    if (this.props.hasAnyFormErrors) {
      const pertinentWarnings = validationErrors.filter(v =>
        v.type === 'warning' &&
        v.components?.some(c => config.validationFormIoComponents?.some(vc => vc === c))
      );

      if (pertinentWarnings.length > 0 && errorStyle === "") {
        errorStyle = "dveValidationWarning";
        iconClass = warningIconClass;
        hasWarning = true;
      }
    }

    const dveSelectStyle = "dveSelect " + errorStyle;
    const textBoxStyle = "dveTextbox dveEditBox " + errorStyle;
    const textAreaStyle = "dveTextArea " + errorStyle;

    const correctedPlaceholder =
      this.state.isLoading ? 'Loading...' :
      this.state.dropdownValues.length === 0 ? 'No options' : '';

    return (
      <React.Fragment>
        <div className="dveContainer">
          <label className="form-label dveLabel">{config.label}</label>

          {/* ORIGINAL VALUE (read-only) */}
          {config.inputType === eFormElementType.Textbox && (
            <input type="text" value={config.originalValue ?? ''} disabled className="dveTextbox" />
          )}

          {config.inputType === eFormElementType.Dropdown && (
            <select
              value={this.state.originalLockedOption?.name ?? (config.originalValue ?? '')}
              disabled
              className="dveSelect"
            >
              {this.state.originalLockedOption
                ? (
                  <option value={this.state.originalLockedOption.name}>
                    {config.formatDropdownValuesToIncludeName
                      ? `${this.state.originalLockedOption.name} - `
                      : ''}
                    {this.state.originalLockedOption.description}
                  </option>
                )
                : (
                  <>
                    <option value="">{this.state.isLoading ? 'Loading...' : ''}</option>
                    {this.state.dropdownValues.map((item, idx) => (
                      <option key={idx} value={item.name}>
                        {config.formatDropdownValuesToIncludeName ? `${item.name} - ` : ""}
                        {item.description}
                      </option>
                    ))}
                  </>
                )
              }
            </select>
          )}

          {config.inputType === eFormElementType.Empty && (
            <div>{config.calculatedValue ?? ""}</div>
          )}

          {config.inputType === eFormElementType.TextArea && (
            <textarea disabled className="dveTextArea">
              {config.originalValue ?? ""}
            </textarea>
          )}

          <br />

          {/* CORRECTED VALUE (editable) */}
          {config.inputType === eFormElementType.Textbox && (
            <input
              type="text"
              value={config.correctedValue ?? ''}
              className={textBoxStyle}
              disabled={
                this.props.readOnly ||
                (config.correctedValueEditable !== undefined && config.correctedValueEditable === false)
              }
              onChange={this.props.onChange}
            />
          )}

          {config.allowValueClearing && (
            <div className='px-2 mx-1'>
              <input 
                type="checkbox" 
                value=""
                onChange={this.props.onChange} />
              <label className="px-2">Clear existing value</label>
            </div>
          )}

          {config.inputType === eFormElementType.Dropdown && (
            <select
              value={config.correctedValue ?? ''}
              className={dveSelectStyle}
              disabled={
                this.props.readOnly ||
                (config.correctedValueEditable !== undefined && config.correctedValueEditable === false)
              }
              onChange={this.props.onChange}
            >
              <option value="">{correctedPlaceholder}</option>
              {this.state.dropdownValues.map((item, idx) => (
                <option key={idx} value={item.name}>
                  {config.formatDropdownValuesToIncludeName ? `${item.name} - ` : ""}
                  {item.description}
                </option>
              ))}
            </select>
          )}

          {config.inputType === eFormElementType.TextArea && !config.enableEditing && (
            <textarea
              className={textAreaStyle}
              disabled={
                this.props.readOnly ||
                (config.correctedValueEditable !== undefined && config.correctedValueEditable === false)
              }
              value={config.correctedValue ?? ''}
              onChange={this.props.onChange}
            />
          )}

          {config.inputType === eFormElementType.TextArea && config.enableEditing && (
            <React.Fragment>
              <textarea
                className={textAreaStyle}
                disabled={this.props.readOnly || !this.state.isEditingTextArea}
                value={config.correctedValue ?? ''}
                onChange={this.handleEditableTextAreaChange}
                onBlur={() => this.setState({ isEditingTextArea: false })}
              />
              &nbsp;&nbsp;
              {!this.props.readOnly && !this.state.isEditingTextArea && (
                <i
                  className="pointer"
                  onClick={() => this.setState({ isEditingTextArea: true })}
                >
                  <img src={window.location.origin + require('../../assets/edit.png')} />
                </i>
              )}
              {this.state.showLengthError && (
                <span>Location cannot have more than 100 characters</span>
              )}
            </React.Fragment>
          )}

          {(hasError || hasWarning) && (
            <TooltipToggle
              key={config.label}
              iconClass={iconClass}
              placment="top"
              validations={pertinentValidations}
            />
          )}
        </div>
      </React.Fragment>
    );
  }
}
